import React, { useState, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Header } from "./components/Header/Header";
import { HomePage } from "./pages/HomePage/HomePage";
import { DetailsPage } from "./pages/DetailsPage/DetailsPage";
import { AdminPanelPage } from "./pages/AdminPanelPage/AdminPanelPage";
import AuthGuard from "./auth/AuthGuard";
import { FilterProvider } from "./context/FilterContext";
import { DefaultFiltersProvider } from "./context/DefaultFiltersContext";
import { UserProvider } from "./context/UserContext";
import { ToastContainer } from "react-toastify";
import { FavoriteListProvider } from "./context/FavoriteListContext";
import { RulesOfTheRoadModal } from "./components/RulesOfTheRoadModal/RulesOfTheRoadModal";

const queryClient = new QueryClient();

const AppLayout: React.FC<{
  darkMode: boolean;
  onToggleDarkMode: () => void;
}> = ({ darkMode, onToggleDarkMode }) => {
  const location = useLocation();

  // ✅ Admin panel pages manage their own spacing (do NOT add global padding)
  const isAdmin = location.pathname.startsWith("/admin_panel");

  return (
    <div className="flex flex-col min-h-screen">
      <Header darkMode={darkMode} setDarkMode={onToggleDarkMode} />

      {/* App background / sizing container */}
      <div className="flex-grow flex flex-col bg-slate-50 dark:bg-slate-950/20 min-h-0">
        {/* ✅ Restore old padding for main pages only */}
        <div className={isAdmin ? "flex-1 min-h-0" : "flex-1 min-h-0 p-4"}>
          <Routes>
            <Route path="/" element={<Navigate to="/recent" />} />
            <Route path="/recent" element={<HomePage />} />
            <Route path="/favorite" element={<HomePage />} />
            <Route path="/details/:regulator/:id" element={<DetailsPage />} />

            <Route
              path="/admin_panel/*"
              element={<AdminPanelPage darkMode={darkMode} />}
            >
              <Route path="prompt_library" element={null} />
              <Route path="user_settings" element={null} />
              <Route path="manage_users" element={null} />
              <Route path="notifications" element={null} />
            </Route>

            <Route path="/chatAI" element={<HomePage />} />
          </Routes>
        </div>
      </div>

      <ToastContainer />
      <RulesOfTheRoadModal />
    </div>
  );
};

const App: React.FC = () => {
  const [darkMode, setDarkMode] = useState(false);

  const handleToggleDarkMode = () => {
    setDarkMode((prevMode) => {
      const newMode = !prevMode;
      localStorage.setItem("darkMode", JSON.stringify(newMode));
      return newMode;
    });
  };

  useEffect(() => {
    const savedMode = localStorage.getItem("darkMode");
    if (savedMode !== null) {
      setDarkMode(JSON.parse(savedMode));
    }
  }, []);

  useEffect(() => {
    if (darkMode) document.documentElement.classList.add("dark");
    else document.documentElement.classList.remove("dark");
  }, [darkMode]);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <UserProvider>
          <AuthGuard>
            <FilterProvider>
              <DefaultFiltersProvider>
                <FavoriteListProvider>
                  <AppLayout
                    darkMode={darkMode}
                    onToggleDarkMode={handleToggleDarkMode}
                  />
                </FavoriteListProvider>
              </DefaultFiltersProvider>
            </FilterProvider>
          </AuthGuard>
        </UserProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default App;